import { useCallback, useMemo, useState } from 'react'
import { Col, Row, Spinner } from 'react-bootstrap'
import { useFormContext } from 'react-hook-form'

import { Button } from '../../components/Button/Button'
import { LinkFolderModal } from '../../components/DocumentFileBrowser/LinkFolderModal'
import { FolderPathLabel } from '../../components/DocumentFileBrowser/FolderPathLabel'
import { buildFolderPathSegments } from '../../components/DocumentFileBrowser/utils'
import { getIntegrationDisplayName, type IntegrationServiceId } from '../../common/integrations'
import { selectAccountId } from '../../store/auth/authSlice'
import { useAppSelector } from '../../store/hooks'
import { useListDocumentConnectorItemsQuery, type DocumentConnectorIntegration } from '../../store/documentConnectors/documentConnectors'
import { type User, type UserCreateUpdateIntegrations } from '../../models/User'
import { getUserDisplayName } from '../../apps/professional-app/fa/common/aifeatures/utils'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { DocumentFolderMapSource } from '../../common/useLinkContactFolder'

interface FormValues {
  integrations: UserCreateUpdateIntegrations[]
}

interface DocumentFolderLinkingSectionProps {
  serviceId: IntegrationServiceId
  contact: User
}

export const DocumentFolderLinkingSection = ({ serviceId, contact }: DocumentFolderLinkingSectionProps) => {
  const accountId = useAppSelector(selectAccountId)
  const { getValues, setValue, watch } = useFormContext<FormValues>()
  const integrations = watch('integrations')
  const [showModal, setShowModal] = useState(false)
  const { trackingClient } = useTracking()

  const contactName = getUserDisplayName(contact)
  const displayName = getIntegrationDisplayName(serviceId)

  const linkedFolder = useMemo(() => integrations.find((i) => i.type === 'document' && i.serviceId === serviceId), [integrations, serviceId])
  const folderId = linkedFolder?.externalId
  const folderIds = useMemo(() => (folderId ? [folderId] : []), [folderId])
  const initialFolderIds = useMemo(() => (folderId ? [folderId] : undefined), [folderId])

  const { data, isFetching, isError } = useListDocumentConnectorItemsQuery({ accountId, connector: serviceId, folderIds }, { skip: !folderId })

  const folder = folderId ? data?.[folderId]?.folder : undefined
  const pathSegments = folder ? buildFolderPathSegments(folder) : null
  const fallbackName = folderId && isError ? `Folder ${folderId}` : ''

  const handleFolderSelected = useCallback(
    (folder: { id: string; name: string; integration?: DocumentConnectorIntegration }) => {
      if (!folder.integration) {
        return
      }
      const current = getValues('integrations') ?? []
      const existing = current.find((i) => i.serviceId === serviceId && i.type === 'document')
      const filtered = current.filter((i) => !(i.serviceId === serviceId && i.type === 'document'))
      const newIntegration: UserCreateUpdateIntegrations = {
        id: existing?.id,
        type: folder.integration.type,
        serviceId: folder.integration.serviceId,
        externalId: folder.integration.externalId,
        externalAccountId: folder.integration.externalAccountId ?? '',
      }
      setValue('integrations', [...filtered, newIntegration])
      trackingClient.track({
        eventName: TrackEvent.DOCUMENT_FOLDER_MAPPED_TO_CONTACT,
        eventDetails: { system: serviceId, source: DocumentFolderMapSource.EDIT_CONTACT },
      })
    },
    [getValues, serviceId, setValue, trackingClient],
  )

  const handleRemoveFolder = useCallback(() => {
    const current = getValues('integrations') ?? []
    setValue(
      'integrations',
      current.filter((i) => !(i.serviceId === serviceId && i.type === 'document')),
    )
  }, [getValues, serviceId, setValue])

  return (
    <Row>
      <Col>
        <label className='form-label'>Linked {displayName} Folder</label>
        {linkedFolder ? (
          <div className='d-flex align-items-center gap-2'>
            <button type='button' className='form-control d-flex align-items-center text-start' onClick={() => setShowModal(true)}>
              {isFetching ? (
                <Spinner size='sm' />
              ) : pathSegments ? (
                <FolderPathLabel segments={pathSegments} className='flex-grow-1' />
              ) : (
                <span className='flex-grow-1 text-truncate'>{fallbackName}</span>
              )}
              <i className='bi bi-chevron-right ms-auto zocks-text-gray-400' />
            </button>
            <Button variant='primaryGhost' className='zocks-p-0.75 flex-shrink-0' onClick={handleRemoveFolder}>
              <i className='bi bi-trash' />
            </Button>
          </div>
        ) : (
          <button type='button' className='form-control d-flex align-items-center text-start' onClick={() => setShowModal(true)}>
            <span className='flex-grow-1 zocks-text-gray-400'>Link Folder in {displayName}</span>
            <i className='bi bi-chevron-right ms-auto zocks-text-gray-400' />
          </button>
        )}
        {showModal ? (
          <LinkFolderModal
            show={showModal}
            onHide={() => setShowModal(false)}
            serviceId={serviceId}
            contactName={contactName}
            onFolderSelected={handleFolderSelected}
            initialFolderIds={initialFolderIds}
          />
        ) : null}
      </Col>
    </Row>
  )
}
